import { FileDownloadOutlined, UploadFileRounded } from '@mui/icons-material';
import { CircularProgress, Stack } from '@mui/material';
import {
  Button,
  Checkbox,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { SELECT_ACTION, adminTab } from '~/types/enum';
import { cell, loading } from './ResourceTable.style';
import { uniq, uniqueId } from 'lodash';
import { TSort } from '~/types/common.type';
import { useSelector } from 'react-redux';
import { useEffect, useRef, useState } from 'react';
import { AdminDialog } from '~/components/Admin/AdminDialog/AdminDialog';
import { HEADERS } from '~/views/Admin/Admin.constant';
import { admin } from '~/views/Admin/Admin.style';
import { ResourceRow } from './ResourceRow';
import { TableHeaderCell } from '~/components/Admin/TableHeaderCell';
import {
  getterAdminResourceFilter,
  getterAdminResources,
  getterAdminResourcesPayload,
  getterAdminDownloadList,
} from '~/store/resource/resourceGetter';
import { TAdminResource } from '~/store/resource/resource.type';
import {
  useGetResourcesQuery,
  useUpdateLaunchStatusMutation,
} from '~/api/oneBoxV3';
import { buildData, handleExportExcel } from '~/utils/helper/file';
import {
  resourceCategory,
  resourceEduCode,
  resourceVersion,
} from '~/api/oneBoxV3/oneBoxV3.type';
import { useAppDispatch } from '~/store';
import { setSearchCondition } from '~/store/resource';
import { EditDialogContent } from '../AdminDialog/EditDialogContent';
import { AdminDialogActions } from '../AdminDialog/AdminDialogActions';
import { UploadDialogContent } from '../AdminDialog/UploadDialogContent';
import { getTitleText } from '~/utils/helper/display';
import { BaseSnackbar } from '~/components/Global/Snackbar';

type TProps = {
  sort: TSort<string>;
  handleSort: (property: string) => void;
  tab: adminTab;
  setTotalPageCount: (count: number) => void;
};

export type THandleSelectedResources = {
  type: SELECT_ACTION;
  resource?: TAdminResource;
};

export enum ACTION_STATUS {
  NON,
  SELECT,
  EDIT,
  BATCH_ON,
  BATCH_OFF,
  UPLOAD,
}

export type THandleFileDownload = {
  originalFileId: string;
  createdAt: string;
  categories: resourceCategory[];
  versions: resourceVersion[];
  years: string[];
  eduCodes: resourceEduCode[];
  subjects: string[];
  grades: string[];
  resources1: string[];
  resources2: string[];
  resources3: string[];
  fileName: string;
};

export const ResourceTable = ({
  sort,
  handleSort,
  tab,
  setTotalPageCount,
}: TProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const dispatch = useAppDispatch();
  const { isLaunch, pagination } = useSelector(getterAdminResourceFilter);
  const resourcePayload = useSelector(getterAdminResourcesPayload);
  const [actionStatus, setActionStatus] = useState<ACTION_STATUS>(
    ACTION_STATUS.NON,
  );
  const [selectedResources, setSelectedResources] = useState<TAdminResource[]>(
    [],
  );
  const [showSnackbar, setShowSnackbar] = useState<{
    isShow: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    isShow: false,
    message: '',
    severity: 'success',
  });
  const memoizedPageRef = useRef({
    launched: { page: 1, pageSize: 10 },
    notLaunched: { page: 1, pageSize: 10 },
  });

  useGetResourcesQuery(resourcePayload, { refetchOnMountOrArgChange: true });

  const { resources, totalResourcesCount } = useSelector(state =>
    getterAdminResources(state, resourcePayload),
  );

  useEffect(() => {
    setTotalPageCount(totalResourcesCount);
  }, [totalResourcesCount]);

  const downloadList = useSelector(state =>
    getterAdminDownloadList(state, resourcePayload),
  );

  const handleSelectedResources = ({
    type,
    resource,
  }: THandleSelectedResources) => {
    switch (type) {
      case SELECT_ACTION.ALL:
        setSelectedResources(resources);
        break;
      case SELECT_ACTION.DELETE:
        resource &&
          setSelectedResources(prev =>
            prev.filter(res => res._id !== resource._id),
          );
        break;
      case SELECT_ACTION.RESET:
        setSelectedResources([]);
        break;
      case SELECT_ACTION.ADD:
        resource && setSelectedResources(prev => uniq([...prev, resource]));
        break;

      case SELECT_ACTION.EDIT:
        resource && setSelectedResources([resource]);
        break;
      default:
        break;
    }
  };
  const handleFileDownload = (downloadData: THandleFileDownload[]) => {
    const csvTitle = [
      'id',
      '上傳時間',
      '類別',
      '版本',
      '學年度',
      '學制',
      '科目',
      '年級',
      '資源類別1',
      '資源類別2',
      '資源類別3',
      '顯示名稱',
      '檔名',
      '章節關鍵字',
    ];
    const res = buildData<THandleFileDownload>(downloadData, csvTitle);
    handleExportExcel(res);
  };

  const handleTabChange = (
    event: React.MouseEvent<HTMLElement>,
    newValue: boolean | null,
  ) => {
    // exclusive 的 ToggleButtonGroup 在重複點選已選取項目時會帶 null，
    // 直接送出會清空上架狀態篩選，這裡忽略不處理
    if (newValue === null) return;
    const { page, pageSize } = pagination;
    if (isLaunch) {
      memoizedPageRef.current.launched = { page, pageSize };
    } else {
      memoizedPageRef.current.notLaunched = { page, pageSize };
    }
    const { launched, notLaunched } = memoizedPageRef.current;
    dispatch(
      setSearchCondition({
        isLaunch: newValue,
        page: isLaunch ? notLaunched.page : launched.page,
        pageSize: isLaunch ? notLaunched.pageSize : launched.pageSize,
      }),
    );
  };

  useEffect(() => {
    setActionStatus(ACTION_STATUS.NON);
    handleSelectedResources({ type: SELECT_ACTION.RESET });
  }, [tab]);

  const [updateLaunchApi] = useUpdateLaunchStatusMutation();

  const handleClose = () => {
    switch (actionStatus) {
      case ACTION_STATUS.BATCH_OFF:
      case ACTION_STATUS.BATCH_ON:
        setActionStatus(ACTION_STATUS.SELECT);
        break;
      case ACTION_STATUS.EDIT:
        setActionStatus(ACTION_STATUS.NON);
        handleSelectedResources({ type: SELECT_ACTION.RESET });
        break;
      default:
        setActionStatus(ACTION_STATUS.NON);
    }
  };

  const handleSubmit = async () => {
    const idList = selectedResources.map(res => res._id);
    const isLaunchAction = actionStatus === ACTION_STATUS.BATCH_ON;
    const actionText = isLaunchAction ? '上' : '下';
    try {
      const { err } = await updateLaunchApi({
        ids: idList,
        isLaunch: isLaunchAction,
      }).unwrap();

      if (err !== 0) {
        setShowSnackbar({
          message: `資源${actionText}架失敗，請稍後再試。`,
          isShow: true,
          severity: 'error',
        });
        return;
      }

      setShowSnackbar({
        message: `資源已${actionText}架成功！`,
        isShow: true,
        severity: 'success',
      });
      setActionStatus(ACTION_STATUS.NON);
      handleSelectedResources({ type: SELECT_ACTION.RESET });
    } catch {
      setShowSnackbar({
        message: `資源${actionText}架失敗，請稍後再試。`,
        isShow: true,
        severity: 'error',
      });
    }
  };

  useEffect(() => {
    setIsLoading(true);
  }, [resources]);

  useEffect(() => {
    if (resources.length >= 0) {
      setTimeout(() => {
        setIsLoading(false);
      }, 1000);
    }
  }, [resources]);

  return (
    <>
      <admin.TopContainer>
        <ToggleButtonGroup
          color="info"
          value={isLaunch}
          exclusive
          onChange={handleTabChange}
          aria-label="Platform"
        >
          <ToggleButton value={false}>未上架</ToggleButton>
          <ToggleButton value={true}>已上架</ToggleButton>
        </ToggleButtonGroup>
        <Typography variant="h6" sx={{ marginRight: 'auto' }}>
          {tab}
        </Typography>
        {tab !== adminTab.record && (
          <admin.ActionContainer>
            {selectedResources.length > 0 ? (
              <>
                <Button
                  color="inherit"
                  variant="outlined"
                  onClick={() => {
                    setActionStatus(ACTION_STATUS.NON);
                    handleSelectedResources({ type: SELECT_ACTION.RESET });
                  }}
                >
                  清除選取
                </Button>
                <Button
                  variant="contained"
                  color={!isLaunch ? 'info' : 'success'}
                  sx={{
                    '&:hover': {
                      backgroundColor: !isLaunch ? 'info.dark' : 'success.dark',
                    },
                  }}
                  onClick={() => {
                    setActionStatus(prev => {
                      if (prev === ACTION_STATUS.SELECT) {
                        return isLaunch
                          ? ACTION_STATUS.BATCH_OFF
                          : ACTION_STATUS.BATCH_ON;
                      }
                      return prev;
                    });
                  }}
                >
                  批次{isLaunch ? '下' : '上'}架
                </Button>
              </>
            ) : (
              <>
                <admin.IconButton
                  variant="outlined"
                  color="inherit"
                  onClick={() => {
                    handleFileDownload(downloadList);
                  }}
                >
                  <FileDownloadOutlined />
                  表單下載
                </admin.IconButton>
                <admin.IconButton
                  variant="outlined"
                  color="inherit"
                  onClick={() => {
                    setActionStatus(ACTION_STATUS.UPLOAD);
                  }}
                >
                  <UploadFileRounded />
                  表單上傳
                </admin.IconButton>
              </>
            )}
          </admin.ActionContainer>
        )}
      </admin.TopContainer>
      {isLoading ? (
        <loading.stackContainer>
          <Stack justifyContent="center" alignItems="center">
            <CircularProgress sx={{ color: '#3366FF' }} />
            <div>載入中...</div>
          </Stack>
        </loading.stackContainer>
      ) : (
        <TableContainer style={{ maxWidth: '100%' }}>
          <Table>
            <TableHead>
              <TableRow>
                {
                  <cell.Container>
                    <Checkbox
                      checked={
                        selectedResources.length === resources.length &&
                        selectedResources.length !== 0 &&
                        resources.length !== 0
                      }
                      indeterminate={
                        selectedResources.length > 0 &&
                        selectedResources.length < resources.length
                      }
                      sx={{ padding: '0px' }}
                      color="info"
                      onChange={(event, checked) => {
                        setActionStatus(() => {
                          return checked
                            ? ACTION_STATUS.SELECT
                            : ACTION_STATUS.NON;
                        });
                        handleSelectedResources({
                          type: checked
                            ? SELECT_ACTION.ALL
                            : SELECT_ACTION.RESET,
                        });
                      }}
                    />
                  </cell.Container>
                }
                {HEADERS.resource
                  .filter(item =>
                    actionStatus === ACTION_STATUS.NON ? true : !!item.title,
                  )
                  .map(header => (
                    <TableHeaderCell
                      key={uniqueId()}
                      data={header}
                      sort={sort}
                      handleSort={handleSort}
                    />
                  ))}
                <cell.Container
                  sx={{
                    position: 'sticky',
                    right: 0,
                    boxShadow: 'inset 1px 0 0 0 #DFE3E8',
                  }}
                >
                  <cell.HeaderContent />
                </cell.Container>
              </TableRow>
            </TableHead>
            <TableBody>
              {resources.map(data => (
                <ResourceRow
                  key={data._id}
                  data={data}
                  tab={tab}
                  isSelected={
                    !!selectedResources.find(
                      selectedItem => selectedItem._id === data._id,
                    )
                  }
                  selectedResourcesLength={selectedResources.length}
                  handleSelectedResources={handleSelectedResources}
                  setActionStatus={setActionStatus}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <AdminDialog
        open={
          actionStatus !== ACTION_STATUS.NON &&
          actionStatus !== ACTION_STATUS.SELECT
        }
        handleClose={handleClose}
        status={actionStatus}
        titleText={getTitleText(actionStatus, selectedResources.length)}
      >
        {actionStatus === ACTION_STATUS.UPLOAD && (
          <UploadDialogContent handleClose={handleClose} isLaunch={isLaunch} />
        )}
        {actionStatus === ACTION_STATUS.EDIT &&
          selectedResources.length === 1 && (
            <EditDialogContent
              editedResource={selectedResources[0]}
              tab={tab}
              handleClose={handleClose}
            />
          )}
        {(actionStatus === ACTION_STATUS.BATCH_OFF ||
          actionStatus === ACTION_STATUS.BATCH_ON) && (
          <AdminDialogActions
            status={actionStatus}
            handleClose={handleClose}
            handleSubmit={handleSubmit}
            acceptButtonText={`確定${actionStatus === ACTION_STATUS.BATCH_OFF ? '下' : ''}${actionStatus === ACTION_STATUS.BATCH_ON ? '上' : ''}架`}
          />
        )}
      </AdminDialog>
      <BaseSnackbar
        open={showSnackbar.isShow}
        message={showSnackbar.message}
        severity={showSnackbar.severity}
        handleClose={() =>
          setShowSnackbar({ isShow: false, message: '', severity: 'success' })
        }
      />
    </>
  );
};
